import { useNotification } from 'admin';
import { ContextModelNameEnum, DocumentType, useApiDocumentsV1DocumentsCreate, useCurrentUser } from 'api';
import {
  AutocompleteField,
  Form,
  InferType,
  LoadAutoComplete,
  SelectField,
  TradeCompanyAutocomplete,
  useForm,
  yup,
  yupResolver,
} from 'form';
import { Box, Button, Page, Section } from 'ui';

import { DealAutocomplete } from '~/deal/components/DealAutocomplete/DealAutocomplete';
import { DOCUMENT_TYPE_DISPLAYED_VALUE } from '~/document/utils/document';
import { InspectionAutocomplete } from '~/inspection';
import { ShipmentAutocomplete } from '~/logistics/components/ShipmentAutocomplete';
import { PurchaseOrderAutocomplete } from '~/orders/components/PurchaseOrderAutocomplete';
import { SalesOrderAutocomplete } from '~/orders/components/SalesOrderAutocomplete';

const schema = yup.object().shape({
  documentType: yup.string().oneOf(Object.values(DocumentType)).required(),
  entityId: yup.number().required(),
  modelName: yup.string().oneOf(Object.values(ContextModelNameEnum)).required(),
});

type FormValues = InferType<typeof schema>;

export const GenerateDocumentPage = () => {
  const { open } = useNotification();
  const { data: user } = useCurrentUser();
  const { mutate: generateDocument, isPending } = useApiDocumentsV1DocumentsCreate({
    mutation: {
      onError: () => {
        open?.({ message: 'Failed to generate document', type: 'error' });
      },
      onSuccess: ({ id }) => {
        window.location.href = `/ground-control/document-generation/${id}`;
      },
    },
  });

  const form = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  const selectedModel = form.watch('modelName');

  const handleSubmit = (values: FormValues) => {
    if (!user) {
      return;
    }

    generateDocument({
      data: {
        auto_generate_pdf: true,
        context_model_name: values.modelName,
        context_object_id: values.entityId,
        created_by_id: user?.id,
        document_type: values.documentType,
      },
    });
  };

  const handleModelChange = () => {
    form.resetField('entityId');
    form.resetField('documentType');
  };

  // Convert ContextModelNameEnum to options for SelectField
  const modelOptions = Object.entries(ContextModelNameEnum).map(([key, value]) => ({
    label: key,
    value,
  }));

  // Get all document type options
  const documentTypeOptions = Object.entries(DocumentType).map(([_, value]) => ({
    label: DOCUMENT_TYPE_DISPLAYED_VALUE[value],
    value,
  }));

  return (
    <Page>
      <Page.Title title="Generate Document" />
      <Section>
        <Form {...form} onSubmit={handleSubmit}>
          <SelectField
            name="modelName"
            label="Select Model"
            options={modelOptions}
            required
            helperText="Select a model that has documents associated with it"
            onChange={(e) => {
              if (e.target.value) {
                handleModelChange();
              }
            }}
          />

          {selectedModel ? (
            <>
              <EntityAutocompletes selectedModel={selectedModel} form={form} />
              <Box mt={2}>
                <AutocompleteField
                  name="documentType"
                  label="Select Document Type"
                  options={documentTypeOptions}
                  required
                  helperText="Select a document type to generate"
                />
              </Box>
            </>
          ) : null}

          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button type="submit" loading={isPending}>
              Generate Document
            </Button>
          </Box>
        </Form>
      </Section>
    </Page>
  );
};

const EntityAutocompletes = ({
  selectedModel,
  form,
}: {
  selectedModel: ContextModelNameEnum | null;
  form: ReturnType<typeof useForm<FormValues>>;
}) => {
  if (!selectedModel) return null;

  switch (selectedModel) {
    case ContextModelNameEnum.TradeCompany:
      return (
        <TradeCompanyAutocomplete
          name="entityId"
          label="Select Company"
          required
          onSelectOption={(company) => {
            if (company) {
              form.setValue('entityId', company.id);
            }
          }}
        />
      );
    case ContextModelNameEnum.Deal:
      return (
        <Box mt={2}>
          <DealAutocomplete
            required
            onSelectDeal={(deal) => {
              if (deal) {
                form.setValue('entityId', deal.id);
              }
            }}
          />
        </Box>
      );
    case ContextModelNameEnum.Load:
      return (
        <LoadAutoComplete
          label="Select Load"
          name="entityId"
          required
          onSelectOption={(load) => {
            if (load) {
              form.setValue('entityId', load.id);
            }
          }}
        />
      );
    case ContextModelNameEnum.Inspection:
      return (
        <InspectionAutocomplete
          name="entityId"
          label="Select Inspection"
          required
          onSelectOption={(inspection) => {
            if (inspection) {
              form.setValue('entityId', inspection.id);
            }
          }}
        />
      );
    case ContextModelNameEnum.PurchaseOrder:
      return (
        <PurchaseOrderAutocomplete
          name="entityId"
          label="Select Purchase Order"
          required
          onSelectOption={(order) => {
            if (order) {
              form.setValue('entityId', order.id);
            }
          }}
        />
      );
    case ContextModelNameEnum.SalesOrder:
      return (
        <SalesOrderAutocomplete
          name="entityId"
          label="Select Sales Order"
          required
          onSelectOption={(order) => {
            if (order) {
              form.setValue('entityId', order.id);
            }
          }}
        />
      );
    case ContextModelNameEnum.Shipment:
      return (
        <ShipmentAutocomplete
          name="entityId"
          label="Select Shipment"
          required
          onSelectOption={(shipment) => {
            if (shipment) {
              form.setValue('entityId', shipment.id);
            }
          }}
        />
      );
    default:
      return null;
  }
};
